import React, {
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  useRef,
} from 'react';
import { uuidv4 } from 'utils';

type PartialIncludes<T, K extends keyof T> = Partial<T> & Pick<T, K>;

export interface IToastConfig {
  type: string;
  title: string;
  message?: string;
  duration: number;
  position: string;
  id: string;
}

interface IToastContextProps {
  toastList: IToastConfig[];
  showToast: (props: PartialIncludes<IToastConfig, 'title'>) => void;
  deleteToast: (id: string) => void;
  extendToastDuration: (id: string, extraSeconds?: number) => void;
}

const ToastContext = React.createContext<IToastContextProps | null>(null);

export const ToastProvider = ({ children }: PropsWithChildren<null>) => {
  const [toastList, setToastList] = useState<IToastConfig[]>([]);
  const toastTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const remainingTime = useRef<Record<string, number>>({});

  const deleteToast = (id: string) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    delete remainingTime.current[id];
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showToast = ({
    duration = 20,
    type = 'success',
    position = 'topRight',
    ...config
  }: PartialIncludes<IToastConfig, 'title'>) => {
    const id = uuidv4();
    const durationMs = duration * 1000;

    const newToast: IToastConfig = {
      ...config,
      id,
      type,
      position,
      duration: durationMs,
    };

    setToastList((prev) => [...prev, newToast]);

    remainingTime.current[id] = durationMs;

    toastTimers.current[id] = setTimeout(() => {
      deleteToast(id);
    }, durationMs);
  };

  const extendToastDuration = (id: string, extraSeconds = 20) => {
    clearTimeout(toastTimers.current[id]);

    const extraMs = extraSeconds * 1000;
    const newDuration = extraMs;

    remainingTime.current[id] = newDuration;

    toastTimers.current[id] = setTimeout(() => {
      deleteToast(id);
    }, newDuration);
  };

  const providerProps = useMemo(() => {
    return {
      toastList,
      showToast,
      deleteToast,
      extendToastDuration,
    };
  }, [toastList]);

  return (
    <ToastContext.Provider value={providerProps}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const contextValue = useContext(ToastContext);
  return contextValue as IToastContextProps;
};
